import React, { useState } from 'react'
import Modal from '../../modal'
import Button from '../../button'
import styled from 'styled-components'
import { useQuery, useMutation } from '@apollo/react-hooks'
import PrepareEsignMutation from '../../../queries/PrepareEsign'
import CreateSignatureRequestsMutation from '../../../queries/CreateSignatureRequests'
import HelloSignQuery from '../../../queries/HelloSignQuery'
import { RequestType } from '../constants'
import LoadingButton from '../../loading-button'
import Signers from './signers'
import Files from './files'
import RequestTypeSelection from './request-type-selection'
import HelloSign from 'hellosign-embedded'
import { useSnackbar } from './../../../contexts/SnackbarContext'
// TODO: Docusign?

const ButtonsContainerElement = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SubtitleElement = styled.h4`
  font-size: 1rem !important;
  font-weight: 200 !important;
  margin-top: 1.2rem !important;
  margin-bottom: 0.8rem !important;
`

const ContentWrapperElement = styled.div`
  width: 45rem;
`

function PrepareEsignModal({
  documentId,
  files,
  guarantors,
  onClose,
  onSuccess,
}) {
  const { setErrorMessage } = useSnackbar()
  const [fileUuidsToSign, setFileUuidsToSign] = useState([])
  const [requestType, setRequestType] = useState(RequestType.EMAIL)
  const [signers, setSigners] = useState(
    guarantors.map(({ name, email }, index) => ({
      id: index + 1,
      name,
      email,
    })),
  )

  const {
    data: { helloSign: helloSignData },
  } = useQuery(HelloSignQuery)

  const [
    createSignatureRequestsMutation,
    { loading: loadingCreateSignatureRequests },
  ] = useMutation(CreateSignatureRequestsMutation, {
    onCompleted: () => onSuccess(requestType),
    onError: () =>
      setErrorMessage('There was an error creating the signature requests'),
  })

  const [prepareEsignMutation, { loading: loadingPrepareEsign }] = useMutation(
    PrepareEsignMutation,
    {
      onCompleted: ({ prepareEsign }) => {
        const helloSignClient = new HelloSign()

        helloSignClient.on('send', () => {
          createSignatureRequestsMutation({
            variables: {
              documentId,
              sendEmail: requestType === RequestType.EMAIL,
            },
          })

          helloSignClient.close()
        })

        const { clientId, testMode } = helloSignData
        const { claimUrl } = prepareEsign.helloSign

        helloSignClient.open(claimUrl, {
          clientId,
          skipDomainVerification: testMode,
        })
      },
      onError: () => setErrorMessage('There was an error preparing the e-sign'),
    },
  )

  const loading = loadingPrepareEsign || loadingCreateSignatureRequests
  const handleClose = () => !loading && onClose()

  const handleSubmit = () =>
    prepareEsignMutation({
      variables: {
        input: {
          documentId,
          documentType: 'template',
          requestMethod: requestType.toLocaleLowerCase(),
          file: null,
          signers: signers
            .filter((s) => s.name && s.email)
            .map(({ name, email }) => ({
              name,
              email,
              role: 'signer',
              label: 'Authorized Signer',
            })),
        },
      },
    })

  return (
    <Modal title="Prepare document for eSignature" onClose={handleClose}>
      <ContentWrapperElement>
        <SubtitleElement>Document(s)</SubtitleElement>
        <Files
          files={files.filter((f) => !f.archived)}
          fileUuidsToSign={fileUuidsToSign}
          handleFileUuidsToSignChange={setFileUuidsToSign}
        />
        <SubtitleElement>Who needs to sign?</SubtitleElement>
        <Signers signers={signers} onSignersChange={setSigners} />
        <SubtitleElement>
          How do you want to send the eSignature request?
        </SubtitleElement>
        <RequestTypeSelection
          requestType={requestType}
          handleRequestTypeChange={setRequestType}
        />
        <ButtonsContainerElement>
          <Button color="gray" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            disabled={loading}
            isLoading={loading}
            onClick={handleSubmit}
            width="9.5rem"
          >
            Prepare for signing
          </LoadingButton>
        </ButtonsContainerElement>
      </ContentWrapperElement>
    </Modal>
  )
}

export default PrepareEsignModal
